import React, { useLayoutEffect, useState } from 'react';
import { BANNER_CSS, bannerView, slideCopy } from '../constants/bannerCarouselWidget';

/**
 * Palco do banner rotativo. Na largura total mede a folha do curso,
 * sem usar 100vw (isso fica só na visualização e no Moodle).
 */
export default function RenderBannerCarousel({ content = {} }) {
  const slotRef = React.useRef(null);
  const [bleed, setBleed] = useState(null);
  const [index, setIndex] = useState(0);
  const view = bannerView(content);
  const full = view.span === 'full';
  const count = view.slides.length;
  const safeIndex = ((index % count) + count) % count;
  const slide = slideCopy(view.slides[safeIndex] || {});

  useLayoutEffect(() => {
    if (!full) {
      setBleed(null);
      return undefined;
    }
    const slot = slotRef.current;
    const canvas = slot?.closest('.course-wrapper');
    if (!slot || !canvas) return undefined;
    const measure = () => {
      const slotBox = slot.getBoundingClientRect();
      const canvasBox = canvas.getBoundingClientRect();
      const current = Number(slot.dataset.bleed || 0);
      const naturalLeft = slotBox.left - current;
      const marginLeft = Math.round(canvasBox.left - naturalLeft);
      const width = Math.round(canvasBox.width);
      setBleed((prev) => {
        if (prev && Math.abs(prev.width - width) < 2 && Math.abs(prev.marginLeft - marginLeft) < 2) return prev;
        return { width, marginLeft };
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(canvas);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [full]);

  const go = (delta) => {
    setIndex((current) => {
      const next = current + delta;
      return ((next % count) + count) % count;
    });
  };

  const vars = {
    '--bn-h': `${view.heightPx}px`,
    '--bn-accent': view.accent,
    '--bn-copy-w': `${view.textWidth}%`,
    '--bn-pad': `${view.textPad}px`,
    '--bn-title-gap': `${view.titleSubGap}px`,
    '--bn-sub-gap': `${view.subDescGap}px`,
    '--bn-radius': `${view.radiusPx}px`,
    '--bn-copy-bg': view.textBg,
    '--bn-title-size': `${view.titleSize}px`,
    '--bn-title': view.titleColor,
    '--bn-sub-size': `${view.subtitleSize}px`,
    '--bn-sub': view.subtitleColor,
    '--bn-desc-size': `${view.descSize}px`,
    '--bn-desc': view.descColor,
    '--bn-btn': view.btnBg,
    '--bn-btn-text': view.btnColor,
    '--bn-btn2': view.btn2Color,
  };

  const classes = [
    'madri-banner',
    view.heightMode === 'fixed' ? 'madri-banner--fixed' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={slotRef}
      data-bleed={full && bleed ? bleed.marginLeft : 0}
      style={full && bleed ? { width: bleed.width, marginLeft: bleed.marginLeft, maxWidth: 'none' } : undefined}
    >
      <style>{BANNER_CSS}</style>
      <div className={classes} style={vars}>
        <div className="madri-banner__frame">
          <div className="madri-banner__slide">
            {slide.url ? (
              <img className="madri-banner__img" src={slide.url} alt={slide.title || slide.caption || ''} />
            ) : (
              <div className="madri-banner__ph" />
            )}
            {slide.hasOverlay || slide.title ? (
              <div className={`madri-banner__copy madri-banner__copy--${view.align}`}>
                {slide.title ? <div className="madri-banner__title">{slide.title}</div> : null}
                {slide.subtitle ? <div className="madri-banner__sub">{slide.subtitle}</div> : null}
                {slide.description ? <div className="madri-banner__desc">{slide.description}</div> : null}
                {(slide.btn1Label || slide.btn2Label) ? (
                  <div className="madri-banner__actions">
                    {slide.btn1Label ? <a className="madri-banner__btn" href={slide.btn1Link || '#'} onClick={(event) => event.preventDefault()}>{slide.btn1Label}</a> : null}
                    {slide.btn2Label ? <a className="madri-banner__btn madri-banner__btn--ghost" href={slide.btn2Link || '#'} onClick={(event) => event.preventDefault()}>{slide.btn2Label}</a> : null}
                  </div>
                ) : null}
              </div>
            ) : null}
            {slide.caption ? <div className="madri-banner__caption">{slide.caption}</div> : null}
          </div>
          {view.floats.map((item, floatIndex) => (
            <div
              key={`${item.url}-${floatIndex}`}
              className={`madri-banner__float${item.layer === 'front' ? ' madri-banner__float--front' : ''}`}
              style={{ left: `${item.x}%`, top: `${item.y}%`, width: `${item.width}px` }}
            >
              <img className={item.motion !== 'none' ? `madri-banner__motion--${item.motion}` : undefined} src={item.url} alt="" />
            </div>
          ))}
          {count > 1 && (
            <>
              <button type="button" className="madri-banner__arrow madri-banner__arrow--prev" aria-label="Anterior" onClick={() => go(-1)}>‹</button>
              <button type="button" className="madri-banner__arrow madri-banner__arrow--next" aria-label="Próximo" onClick={() => go(1)}>›</button>
              <div className="madri-banner__dots">
                {view.slides.map((_, dotIndex) => (
                  <button
                    key={dotIndex}
                    type="button"
                    className={`madri-banner__dot${dotIndex === safeIndex ? ' is-active' : ''}`}
                    aria-label={String(dotIndex + 1)}
                    onClick={() => setIndex(dotIndex)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
